import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import {
  BellIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { notificationsAPI, batchesAPI } from '../services/api';
import {
  LoadingSpinner,
  EmptyState,
  StatCard,
  Badge,
  Modal,
  FilterButton,
  DataTable,
  Pagination,
} from '../components/common';
import { formatDateTime } from '../utils/formatters';
import useAuthStore from '../store/authStore';

export default function NotificationsPage() {
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);
  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [notificationToDelete, setNotificationToDelete] = useState(null);
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [audienceFilter, setAudienceFilter] = useState('all');
  const [selectedAudience, setSelectedAudience] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const { data: notificationsData, isLoading } = useQuery({
    queryKey: ['notifications', typeFilter, statusFilter, audienceFilter, currentPage, pageSize],
    queryFn: async () => {
      const params = {
        page: currentPage,
        limit: pageSize,
      };
      if (typeFilter !== 'all') params.type = typeFilter;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (audienceFilter !== 'all') params.audience = audienceFilter;
      const response = await notificationsAPI.getAll(params);
      return response.data;
    },
  });

  const notifications = notificationsData?.data || [];
  const pagination = notificationsData?.pagination || { total: 0, pages: 1 };

  // Define columns for DataTable
  const columns = [
    {
      key: 'title',
      label: 'Title',
      render: (value, row) => value || row.type?.replace('_', ' ').toUpperCase() || 'N/A',
    },
    {
      key: 'body',
      label: 'Message',
      render: (value, row) => value || row.message || 'N/A',
    },
    {
      key: 'audience',
      label: 'Audience',
      render: (value) => (
        <Badge variant={
          value === 'all' ? 'info' :
          value === 'batch' ? 'warning' :
          'default'
        }>
          {value}
        </Badge>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (value) => (
        <Badge variant={
          value === 'sent' ? 'success' :
          value === 'pending' ? 'warning' :
          'danger'
        }>
          {value}
        </Badge>
      ),
    },
    {
      key: 'recipient_phone',
      label: 'Recipient',
      render: (value, row) => value || `User #${row.user_id}` || 'N/A',
    },
    {
      key: 'created_at',
      label: 'Date',
      render: (value) => formatDateTime(value),
    },
  ];

  // Fetch batches for dropdown
  const { data: batches } = useQuery({
    queryKey: ['batches'],
    queryFn: async () => {
      const response = await batchesAPI.getAll();
      return Array.isArray(response.data) ? response.data : (response.data?.data || []);
    },
    enabled: isCreateModalOpen,
  });

  const createMutation = useMutation({
    mutationFn: (data) => notificationsAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['notifications']);
      toast.success('Notification created successfully!');
      setIsCreateModalOpen(false);
      reset();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to create notification');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => notificationsAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['notifications']);
      toast.success('Notification deleted successfully!');
      setIsDeleteModalOpen(false);
      setNotificationToDelete(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to delete notification');
    },
  });

  const handleDelete = (notification) => {
    setNotificationToDelete(notification);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = () => {
    if (notificationToDelete) {
      deleteMutation.mutate(notificationToDelete.id);
    }
  };

  const onSubmit = (data) => {
    const payload = {
      title: data.title,
      body: data.body,
      notification_type: data.notification_type,
      audience: data.audience,
    };

    if (data.audience === 'user' && data.user_id) {
      payload.user_id = parseInt(data.user_id);
    } else if (data.audience === 'batch' && data.batch_id) {
      payload.batch_id = parseInt(data.batch_id);
    }

    createMutation.mutate(payload);
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'payment_reminder':
        return ExclamationCircleIcon;
      case 'draw_notification':
        return InformationCircleIcon;
      case 'winner_notification':
        return CheckCircleIcon;
      default:
        return BellIcon;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'payment_reminder':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'draw_notification':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'winner_notification':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const stats = {
    total: notifications?.length || 0,
    sent: notifications?.filter(n => n.status === 'sent').length || 0,
    pending: notifications?.filter(n => n.status === 'pending').length || 0,
    failed: notifications?.filter(n => n.status === 'failed').length || 0,
  };

  if (isLoading) {
    return <LoadingSpinner text="Loading notifications..." />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Notifications</h1>
          <p className="mt-2 text-sm text-gray-600">
            View and manage system notifications
          </p>
        </div>
        {(user?.role === 'admin' || user?.role === 'staff') && (
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="btn-primary flex items-center gap-2"
          >
            <PlusIcon className="h-5 w-5" />
            Create Notification
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total"
          value={stats.total}
          icon={BellIcon}
          color="primary"
        />
        <StatCard
          title="Sent"
          value={stats.sent}
          icon={CheckCircleIcon}
          color="green"
        />
        <StatCard
          title="Pending"
          value={stats.pending}
          icon={BellIcon}
          color="yellow"
        />
        <StatCard
          title="Failed"
          value={stats.failed}
          icon={BellIcon}
          color="red"
        />
      </div>

      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex gap-2 flex-wrap">
          <FilterButton
            active={typeFilter === 'all'}
            onClick={() => setTypeFilter('all')}
          >
            All Types
          </FilterButton>
          <FilterButton
            active={typeFilter === 'payment_reminder'}
            onClick={() => setTypeFilter('payment_reminder')}
          >
            Payment
          </FilterButton>
          <FilterButton
            active={typeFilter === 'draw_notification'}
            onClick={() => setTypeFilter('draw_notification')}
          >
            Draw
          </FilterButton>
          <FilterButton
            active={typeFilter === 'winner_notification'}
            onClick={() => setTypeFilter('winner_notification')}
          >
            Winner
          </FilterButton>
        </div>
        <div className="flex gap-2 flex-wrap">
          <FilterButton
            active={statusFilter === 'all'}
            onClick={() => setStatusFilter('all')}
          >
            All Status
          </FilterButton>
          <FilterButton
            active={statusFilter === 'sent'}
            onClick={() => setStatusFilter('sent')}
          >
            Sent
          </FilterButton>
          <FilterButton
            active={statusFilter === 'pending'}
            onClick={() => setStatusFilter('pending')}
          >
            Pending
          </FilterButton>
          <FilterButton
            active={statusFilter === 'failed'}
            onClick={() => setStatusFilter('failed')}
          >
            Failed
          </FilterButton>
        </div>
        <div className="flex gap-2 flex-wrap">
          <FilterButton
            active={audienceFilter === 'all'}
            onClick={() => setAudienceFilter('all')}
          >
            All Audiences
          </FilterButton>
          <FilterButton
            active={audienceFilter === 'user'}
            onClick={() => setAudienceFilter('user')}
          >
            User
          </FilterButton>
          <FilterButton
            active={audienceFilter === 'batch'}
            onClick={() => setAudienceFilter('batch')}
          >
            Batch
          </FilterButton>
          <FilterButton
            active={audienceFilter === 'all'}
            onClick={() => setAudienceFilter('all')}
          >
            Broadcast
          </FilterButton>
        </div>
      </div>

      <div className="card">
        {notifications && notifications.length > 0 ? (
          <>
            <DataTable 
              columns={columns} 
              data={notifications}
              actions={(row) => (
                (user?.role === 'admin' || user?.role === 'staff') ? (
                  <button
                    onClick={() => handleDelete(row)}
                    className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                    title="Delete notification"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                ) : null
              )}
            />
            <Pagination
              currentPage={currentPage}
              totalPages={pagination.pages}
              totalItems={pagination.total}
              itemsPerPage={pageSize}
              onPageChange={setCurrentPage}
              onPageSizeChange={setPageSize}
              isLoading={isLoading}
            />
          </>
        ) : (
          <EmptyState
            icon={BellIcon}
            title="No notifications found"
            description={typeFilter !== 'all' || statusFilter !== 'all'
              ? "Try adjusting your filters"
              : "Notifications will appear here"
            }
          />
        )}
      </div>

      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
          reset();
        }}
        title="Create Notification"
        size="md"
      >
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Audience Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Audience *
            </label>
            <select
              {...register('audience', { 
                required: 'Audience is required',
                onChange: (e) => setSelectedAudience(e.target.value)
              })}
              className="input-field"
            >
              <option value="all">All Members</option>
              <option value="batch">Specific Batch</option>
              <option value="user">Specific User</option>
            </select>
            {errors.audience && (
              <p className="mt-1 text-sm text-red-600">{errors.audience.message}</p>
            )}
          </div>

          {/* Conditional: Batch Selection */}
          {selectedAudience === 'batch' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select Batch *
              </label>
              <select
                {...register('batch_id', { 
                  required: selectedAudience === 'batch' ? 'Batch is required' : false 
                })}
                className="input-field"
              >
                <option value="">Select batch...</option>
                {batches?.map((batch) => (
                  <option key={batch.id} value={batch.id}>
                    {batch.batch_number} - {batch.name} ({batch.current_members} members)
                  </option>
                ))}
              </select>
              {errors.batch_id && (
                <p className="mt-1 text-sm text-red-600">{errors.batch_id.message}</p>
              )}
            </div>
          )}

          {/* Conditional: User ID (hidden for now, will be handled via MembersPage) */}
          {selectedAudience === 'user' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                User ID *
              </label>
              <input
                {...register('user_id', {
                  required: selectedAudience === 'user' ? 'User ID is required' : false,
                  min: { value: 1, message: 'Must be valid user ID' }
                })}
                type="number"
                className="input-field"
                placeholder="123"
              />
              {errors.user_id && (
                <p className="mt-1 text-sm text-red-600">{errors.user_id.message}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Tip: Use the notify button next to members in the Members page
              </p>
            </div>
          )}

          {/* Notification Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Type *
            </label>
            <select
              {...register('notification_type', { required: 'Type is required' })}
              className="input-field"
            >
              <option value="">Select type...</option>
              <option value="general">General</option>
              <option value="payment_reminder">Payment Reminder</option>
              <option value="draw_notification">Draw Notification</option>
              <option value="winner_notification">Winner Notification</option>
              <option value="system">System</option>
            </select>
            {errors.notification_type && (
              <p className="mt-1 text-sm text-red-600">{errors.notification_type.message}</p>
            )}
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title *
            </label>
            <input
              {...register('title', { required: 'Title is required' })}
              type="text"
              className="input-field"
              placeholder="Notification title"
            />
            {errors.title && (
              <p className="mt-1 text-sm text-red-600">{errors.title.message}</p>
            )}
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Message *
            </label>
            <textarea
              {...register('body', { required: 'Message is required' })}
              rows={4}
              className="input-field"
              placeholder="Notification message..."
            />
            {errors.body && (
              <p className="mt-1 text-sm text-red-600">{errors.body.message}</p>
            )}
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={() => {
                setIsCreateModalOpen(false);
                reset();
              }}
              className="flex-1 btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isLoading}
              className="flex-1 btn-primary"
            >
              {createMutation.isLoading ? 'Creating...' : 'Create Notification'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setNotificationToDelete(null);
        }}
        title="Delete Notification?"
        size="md"
      >
        <div className="space-y-4">
          {/* Icon */}
          <div className="flex items-center justify-center">
            <div className="relative">
              <div className="h-20 w-20 rounded-full bg-red-100 flex items-center justify-center">
                <BellIcon className="h-12 w-12 text-red-600" />
              </div>
              <div className="absolute -top-1 -right-1 h-8 w-8 rounded-full bg-red-500 flex items-center justify-center">
                <TrashIcon className="h-5 w-5 text-white" />
              </div>
            </div>
          </div>

          {/* Notification Preview */}
          {notificationToDelete && (
            <div className="p-4 bg-gray-50 rounded-lg border-l-4 border-red-500">
              <p className="text-sm font-semibold text-gray-900">
                {notificationToDelete.title || notificationToDelete.type}
              </p>
              <p className="text-xs text-gray-600 mt-1">
                {notificationToDelete.message}
              </p>
              <p className="text-xs text-gray-500 mt-2">
                To: {notificationToDelete.recipient_phone || `User #${notificationToDelete.user_id}`}
              </p>
            </div>
          )}

          {/* Warning */}
          <div className="text-center">
            <p className="text-sm font-semibold text-gray-900">
              This notification will be permanently deleted
            </p>
            <p className="text-xs text-gray-500 mt-1">
              This action cannot be undone
            </p>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={() => {
                setIsDeleteModalOpen(false);
                setNotificationToDelete(null);
              }}
              className="flex-1 btn-secondary"
              disabled={deleteMutation.isLoading}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={confirmDelete}
              disabled={deleteMutation.isLoading}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg transition-colors disabled:opacity-50"
            >
              {deleteMutation.isLoading ? 'Deleting...' : 'Yes, Delete'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
